import React, { useState, useMemo, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { 
  Box, 
  Paper, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  TextField, 
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import ActiveFilterDisplay from '../layout/ActiveFilterDisplay'; 
import ViewRespondentDialog from './ViewRespondentDialog';
import surveyApi from '../../services/surveyApi';
import { 
  themeColors, 
  getSatisfactionColor, 
  calculateAverageSQD, 
  serviceQualityQuestions,
  satisfactionToNumeric,
  satisfactionLabels
} from '../common/constants';

// Helper function to format time from milliseconds to minutes
const formatTimeInMinutes = (milliseconds) => {
  if (!milliseconds || isNaN(milliseconds)) return 'N/A';
  
  // Convert to minutes and round to 1 decimal place
  const minutes = (milliseconds / 60000).toFixed(1);
  
  // Return formatted string
  return `${minutes} min${minutes === '1.0' ? '' : 's'}`;
};

const FeedbackTab = ({ 
  feedbackData, 
  loading, 
  onDeleteSuccess = () => {},
  activeFilters = {},  
  onClearFilters = () => {},  
  isFiltered = false   
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedRespondent, setSelectedRespondent] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [processedData, setProcessedData] = useState([]);

  // Reset page when filters or data changes
  useEffect(() => {
    setPage(0);
  }, [feedbackData, activeFilters, isFiltered, searchTerm]);

  // Process the survey data to calculate SQD scores
  useEffect(() => {
    if (Array.isArray(feedbackData) && feedbackData.length > 0) {
      const processed = feedbackData.map(item => {
        // Extract SQD responses from the survey item
        const sqdResponses = {};
        serviceQualityQuestions.forEach(question => {
          if (item[question.id]) {
            sqdResponses[question.id] = item[question.id];
          }
        });

        // Calculate the SQD score
        const sqdResult = calculateAverageSQD(sqdResponses);
        
        // Add the calculated results to the item
        return {
          ...item,
          satisfaction_rate: sqdResult.average,
          satisfaction: getSatisfactionLabel(sqdResult.average),
          sqdDetails: sqdResult
        };
      });

      setProcessedData(processed);
    } else {
      setProcessedData([]);
    }
  }, [feedbackData]);

  // Helper function to get satisfaction label from score
  const getSatisfactionLabel = (score) => {
    if (!score) return 'N/A';
    
    // Round to nearest whole number for matching
    const roundedScore = Math.round(score);
    
    // Return the corresponding label
    switch(roundedScore) {
      case 5: return 'Strongly Agree';
      case 4: return 'Agree';
      case 3: return 'Neutral';
      case 2: return 'Disagree';
      case 1: return 'Strongly Disagree';
      default: return 'N/A';
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    setIsDeleting(true);
    surveyApi.deleteSurvey(deleteId)
      .then(response => {
        onDeleteSuccess();
        setIsDeleting(false);
        setDeleteDialogOpen(false);
      })
      .catch(error => {
        console.error('Error deleting survey:', error);
        setIsDeleting(false);
        setDeleteDialogOpen(false);
      });
  };

  // Handle pagination change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Apply search filter and activeFilters on top of the processed data
const filteredData = useMemo(() => {
  if (!Array.isArray(processedData)) {
    return [];
  }
  
  // Start with all processed data
  let filtered = [...processedData];
  
  // Apply search term filter if any
  if (searchTerm) {
    const searchLower = searchTerm.toLowerCase();
    filtered = filtered.filter(item => 
      (item.id?.toString().includes(searchTerm)) ||
      (item.client_type?.toLowerCase().includes(searchLower)) ||
      (item.office?.toLowerCase().includes(searchLower)) ||
      (item.service?.toLowerCase().includes(searchLower)) ||
      (item.gender?.toLowerCase().includes(searchLower))
    );
  }
  
  if (!isFiltered) {
    return filtered;
  }
  
  
  if (Object.keys(activeFilters).length > 0) {
    filtered = filtered.filter(item => {
      let matchesAllFilters = true;
      
   
      if (activeFilters.office && item.office !== activeFilters.office) {
        matchesAllFilters = false;
      }
      
      if (activeFilters.sub_office && item.sub_office !== activeFilters.sub_office) {
        matchesAllFilters = false;
      }
      
      if (activeFilters.clientType && item.client_type !== activeFilters.clientType) {
        matchesAllFilters = false;
      }
      
      if (activeFilters.gender && item.gender !== activeFilters.gender) {
        matchesAllFilters = false;
      }
      
    
      if (activeFilters.minAge && (item.age < activeFilters.minAge)) {
        matchesAllFilters = false;
      }
      
      if (activeFilters.maxAge && (item.age > activeFilters.maxAge)) {
        matchesAllFilters = false;
      }
      
     
      if (activeFilters.minSqdScore && 
          (item.satisfaction_rate < parseFloat(activeFilters.minSqdScore))) {
        matchesAllFilters = false;
      }
      
      if (activeFilters.maxSqdScore && 
          (item.satisfaction_rate > parseFloat(activeFilters.maxSqdScore))) {
        matchesAllFilters = false;
      }
      
   
      if (activeFilters.start_date) {
        const startDate = new Date(activeFilters.start_date);
        const itemDate = new Date(item.created_at);
        if (itemDate < startDate) {
          matchesAllFilters = false;
        }
      }
      
      if (activeFilters.end_date) {
        const endDate = new Date(activeFilters.end_date);
     
        endDate.setHours(23, 59, 59, 999);
        const itemDate = new Date(item.created_at);
        if (itemDate > endDate) {
          matchesAllFilters = false;
        }
      }
      
      return matchesAllFilters;
    });
  }
  
  return filtered;
  
}, [processedData, searchTerm, isFiltered, activeFilters]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewRespondent = (respondent) => {
    setSelectedRespondent(respondent);
    setViewDialogOpen(true);
  };

  // Export to CSV
  /* const exportToCSV = () => {
    if (!Array.isArray(filteredData) || filteredData.length === 0) {
      return;
    }
    
    // Prepare CSV header with all SQD questions
    const headers = [
      "ID", "Date", "Client Type", "Gender", "Age", "Email", "Office", "Service", 
      "Overall Satisfaction", "SQD Score", "Time to Complete"
    ];
    
    // Add SQD questions to headers
    serviceQualityQuestions.forEach(question => {
      headers.push(question.label);
    });
    
    // Create CSV content
    const csvContent = "data:text/csv;charset=utf-8," + 
      headers.join(",") + "\n" +
      filteredData.map(row => {
        const basicFields = [
          row.id,
          row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A',
          `"${row.client_type || 'N/A'}"`,
          `"${row.gender || 'N/A'}"`,
          `"${row.age || 'N/A'}"`,
          `"${row.email || 'N/A'}"`,
          `"${row.office || 'N/A'}"`,
          `"${row.service || 'N/A'}"`,
          `"${row.satisfaction || 'N/A'}"`,
          `"${row.satisfaction_rate ? row.satisfaction_rate.toFixed(2) : 'N/A'}"`,
          `"${row.timeSpent ? formatTimeInMinutes(row.timeSpent) : 'N/A'}"`
        ];
        
        // Add SQD responses
        const sqdFields = serviceQualityQuestions.map(question => 
          `"${row[question.id] || 'N/A'}"`
        );
        
        return [...basicFields, ...sqdFields].join(",");
      }).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    
    // Add filter indicator to filename if filtered
    const fileName = isFiltered ? "filtered_survey_feedback.csv" : "survey_feedback.csv";
    link.setAttribute("download", fileName);
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }; */

  const exportToExcel = () => {
    if (!Array.isArray(filteredData) || filteredData.length === 0) {
      return;
    }
    
    // Import SheetJS
    import('xlsx').then(XLSX => {
      // Separate SQD questions by scale
      const serviceQualityQuestions08 = serviceQualityQuestions.filter(q => {
        // Questions with IDs sqd0 through sqd8 are on 0-8 scale
        return q.id === 'sqd0' || q.id === 'sqd1' || q.id === 'sqd2' || q.id === 'sqd3' || 
               q.id === 'sqd4' || q.id === 'sqd5' || q.id === 'sqd6' || q.id === 'sqd7' || 
               q.id === 'sqd8';
      });
      
      const serviceQualityQuestions010 = serviceQualityQuestions.filter(q => {
        // Questions with IDs sqd9 and sqd10 are on 0-10 scale
        return q.id === 'sqd9' || q.id === 'sqd10';
      });
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Create and add the 0-8 scale sheet
      const data08 = createExcelDataForScale(serviceQualityQuestions08, filteredData);
      const ws08 = XLSX.utils.aoa_to_sheet(data08);
      applyStyleToAverageRow(ws08, data08.length, XLSX);
      XLSX.utils.book_append_sheet(wb, ws08, "SQD 0-8 Scale");
      
      // Create and add the 0-10 scale sheet
      const data010 = createExcelDataForScale(serviceQualityQuestions010, filteredData);
      const ws010 = XLSX.utils.aoa_to_sheet(data010);
      applyStyleToAverageRow(ws010, data010.length, XLSX);
      XLSX.utils.book_append_sheet(wb, ws010, "SQD 0-10 Scale");
      
      // Add filter indicator to filename if filtered
      const fileName = isFiltered ? "filtered_survey_feedback.xlsx" : "survey_feedback.xlsx";
      
      // Generate and download Excel file
      XLSX.writeFile(wb, fileName);
    }).catch(error => {
      console.error("Error exporting to Excel:", error);
      alert("Failed to export data. Please try again.");
    });
  };
  
  // Function to create Excel data for a specific scale of questions
  const createExcelDataForScale = (questions, filteredData) => {
    // Prepare headers for this scale
    const headers = [
      "ID", "Date", "Client Type", "Internal or External", "Gender", "Age", "Office", "Service", "Other Service", 
      "Overall Satisfaction", "SQD Score", "Time to Complete"
    ];
    
    // Add SQD questions specific to this scale
    questions.forEach(question => {
      headers.push(`${question.id}. ${question.label} (Numeric)`);
    });
    
    // Create array of arrays for Excel worksheet
    const excelData = [headers];
    
    // Track SQD response sums for calculating averages
    const sqdSums = {};
    const sqdCounts = {};
    
    // Initialize sums and counts for each SQD question
    questions.forEach(question => {
      sqdSums[question.id] = 0;
      sqdCounts[question.id] = 0;
    });
    
    // Add data rows
    filteredData.forEach(row => {
      const rowData = [
        row.id,
        row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A',
        row.client_type || 'N/A',
        row.InternalExternal || 'N/A',
        row.gender || 'N/A',
        row.age || 'N/A',
        row.office || 'N/A',
        row.service || 'N/A',
        row.other_specification || 'N/A',
        row.satisfaction || 'N/A',
        row.satisfaction_rate ? row.satisfaction_rate.toFixed(2) : 'N/A',
        row.timeSpent ? formatTimeInMinutes(row.timeSpent) : 'N/A'
      ];
      
      questions.forEach(question => {
        const textResponse = row[question.id] || 'N/A';
        const numericValue = textResponse !== 'N/A' ? satisfactionToNumeric(textResponse) : 'N/A';
        rowData.push(numericValue);
        
        if (typeof numericValue === 'number') {
          sqdSums[question.id] += numericValue;
          sqdCounts[question.id]++;
        }
      });
      
      excelData.push(rowData);
    });
    
    let totalSqdSum = 0;
    let totalSqdCount = 0;
    
    for (const key in sqdSums) {
      totalSqdSum += sqdSums[key];
      totalSqdCount += sqdCounts[key];
    }
    
    const overallSqdAverage = totalSqdCount > 0 ? (totalSqdSum / totalSqdCount).toFixed(2) : 'N/A';
      // Calculate average time spent
      const avgTimeMs = calculateAverageTimeSpent(filteredData);
      const formattedAvgTime = avgTimeMs !== 'N/A' ? formatTimeInMinutes(avgTimeMs) : 'N/A';
  
    const averageRow = [
      "AVERAGE", // ID column
      "", // Date
      "", // Client Type
      "", // Internal or Exteral
      "", // Gender
      "", // Age
      "", // Office
      "", // Service
      "", // other_specification
      "", // Overall Satisfaction
      overallSqdAverage, // SQD Score
      formattedAvgTime // Time to Complete
    ];
    
    // Add SQD averages
    questions.forEach(question => {
      const average = sqdCounts[question.id] > 0 
        ? (sqdSums[question.id] / sqdCounts[question.id]).toFixed(2) 
        : 'N/A';
      averageRow.push(average);
    });
    
    // Add the average row to the Excel data
    excelData.push(averageRow);
    
    return excelData;
  };
  
  // Function to apply styling to average row
  const applyStyleToAverageRow = (worksheet, lastRowNum, XLSX) => {
    const range = XLSX.utils.decode_range(worksheet['!ref']);
    
    for (let col = 0; col <= range.e.c; col++) {
      const cellRef = XLSX.utils.encode_cell({ r: lastRowNum - 1, c: col });
      
      if (!worksheet[cellRef]) {
        worksheet[cellRef] = { t: 's', v: '' };
      }
      
      if (!worksheet[cellRef].s) worksheet[cellRef].s = {};
      
      // Add bold and gray background styling
      worksheet[cellRef].s = {
        font: { bold: true },
        fill: { fgColor: { rgb: "EEEEEE" } }
      };
    }
  };

    // Function to calculate average time spent
    const calculateAverageTimeSpent = (filteredData) => {
      if (!Array.isArray(filteredData) || filteredData.length === 0) {
        return 'N/A';
      }
      
      let totalTimeMs = 0;
      let validTimeEntries = 0;
      
      filteredData.forEach(row => {
        if (row.timeSpent && typeof row.timeSpent === 'number') {
          totalTimeMs += row.timeSpent;
          validTimeEntries++;
        }
      });
      
      if (validTimeEntries === 0) {
        return 'N/A';
      }
      
      // Calculate average in milliseconds
      return totalTimeMs / validTimeEntries;
    };

  // Create a debug message with filter info
  const debugFilterInfo = () => {
    if (!isFiltered) return null;
    
    return (
      <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, display: 'none' }}>
        <Typography variant="subtitle2">Debug Filter Info:</Typography>
        <pre>
          {JSON.stringify({ 
            activeFilters,
            isFiltered,
            resultCount: filteredData.length,
            totalProcessed: processedData.length
          }, null, 2)}
        </pre>
      </Box>
    );
  };

  return (
    <Box>
      {/* Search Panel */}
      <Paper sx={{ mb: 3, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search"
              size="small"
              placeholder="Search by keyword..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12} md={6} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              startIcon={<DownloadIcon />}
              onClick={exportToExcel}
              disabled={!Array.isArray(filteredData) || filteredData.length === 0}
              sx={{ 
                bgcolor: themeColors.primary,
                '&:hover': {
                  bgcolor: themeColors.primaryLight
                }
              }}
            >
              Export to Excel
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Display active filters if any */}
      {isFiltered && (
        <ActiveFilterDisplay 
          activeFilters={activeFilters}
          onClearFilters={onClearFilters}
          resultCount={filteredData.length}
        />
      )}
      
      {/* Debugging filter info - remove in production */}
      {debugFilterInfo()}
      
      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Client Type</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell>Age</TableCell>
              <TableCell>Office</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>Satisfaction</TableCell>
              <TableCell>SQD Score</TableCell>
              <TableCell>Time Spent</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  <CircularProgress size={24} /> Loading...
                </TableCell>
              </TableRow>
            ) : !Array.isArray(filteredData) || filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={11} align="center">
                  {isFiltered ? 
                    'No data matches your filter criteria' : 
                    searchTerm ? 
                      'No results match your search term' : 
                      'No data found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow
                    key={row.id}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell component="th" scope="row">{row.id}</TableCell>
                    <TableCell>{row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A'}</TableCell>
                    <TableCell>{row.client_type || 'N/A'}</TableCell>
                    <TableCell>{row.gender || 'N/A'}</TableCell>
                    <TableCell>{row.age || 'N/A'}</TableCell>
                    <TableCell>{row.office || 'N/A'}</TableCell>
                    <TableCell>{row.service || 'N/A'}</TableCell>
                    <TableCell>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        color: getSatisfactionColor(row.satisfaction_rate || 0)
                      }}>
                        {renderSatisfactionEmoji(row.satisfaction)}
                        <Typography variant="body2" sx={{ ml: 0.5 }}>
                          {row.satisfaction || 'N/A'}
                        </Typography> 
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: getSatisfactionColor(row.satisfaction_rate || 0),
                          fontWeight: 'bold'
                        }}
                      >
                        {row.satisfaction_rate ? row.satisfaction_rate.toFixed(2) : 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.timeSpent ? formatTimeInMinutes(row.timeSpent) : 'N/A'}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleViewRespondent(row)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(row.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={Array.isArray(filteredData) ? filteredData.length : 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this survey response? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <ViewRespondentDialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)} 
        respondent={selectedRespondent} 
      />
    </Box>
  );
};

// Helper function to render satisfaction emoji based on satisfaction value
const renderSatisfactionEmoji = (satisfaction) => {
  switch(satisfaction) {
    case 'Strongly Agree': return '😊';
    case 'Agree': return '🙂';
    case 'Neutral': return '😐';
    case 'Disagree': return '🙁';
    case 'Strongly Disagree': return '😠';
    default: return '';
  }
};

export default FeedbackTab;